import React, { useEffect } from "react";

import {
  FaCheck,
  FaFilter,
  FaSearch,
  FaTimes,
  FaTrashAlt,
} from "react-icons/fa";

function FilterGroup({
  title,
  values = [],
  selectedValues = [],
  onToggle,
  onSelectAll,
  onClear,
  columns = "grid-cols-1 sm:grid-cols-2",
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-slate-900">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-xs font-black uppercase tracking-[0.12em] text-slate-700 dark:text-slate-200">
            {title}
          </h3>

          {selectedValues.length > 0 && (
            <p className="mt-0.5 text-[10px] font-medium text-red-600 dark:text-red-400">
              {selectedValues.length} sélection
              {selectedValues.length > 1
                ? "s"
                : ""}
            </p>
          )}
        </div>

        {values.length > 0 && (
          <div className="flex items-center gap-2 text-[11px]">
            <button
              type="button"
              onClick={onSelectAll}
              className="font-bold text-red-600 hover:underline dark:text-red-400"
            >
              Tout
            </button>

            <span className="text-slate-300 dark:text-slate-700">
              •
            </span>

            <button
              type="button"
              onClick={onClear}
              className="font-bold text-slate-500 hover:underline dark:text-slate-400"
            >
              Aucun
            </button>
          </div>
        )}
      </div>

      {values.length === 0 ? (
        <p className="text-xs text-slate-400">
          Aucune valeur disponible.
        </p>
      ) : (
        <div
          className={[
            "grid max-h-56 gap-1.5 overflow-y-auto pr-1",
            columns,
          ].join(" ")}
        >
          {values.map((value) => {
            const checked =
              selectedValues.includes(value);

            return (
              <label
                key={value}
                className={[
                  "flex min-w-0 cursor-pointer items-center gap-2 rounded-xl border px-2.5 py-2",
                  "transition duration-200",
                  checked
                    ? [
                        "border-red-200 bg-red-50 text-red-800",
                        "dark:border-red-900 dark:bg-red-950/30 dark:text-red-200",
                      ].join(" ")
                    : [
                        "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                        "dark:border-white/10 dark:bg-slate-800 dark:text-slate-200",
                        "dark:hover:bg-white/10",
                      ].join(" "),
                ].join(" ")}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    onToggle(value)
                  }
                  className="checkbox checkbox-xs shrink-0"
                />

                <span className="truncate text-xs font-semibold">
                  {value}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default function CalendarFiltersDrawer({
  isOpen,
  onClose,
  filteredEventsCount = 0,
  filters,
}) {
  const {
    AGE_CATEGORIES,

    search,
    setSearch,

    selectedDisciplines,
    setSelectedDisciplines,

    selectedAges,
    setSelectedAges,

    selectedOrganisateurs,
    setSelectedOrganisateurs,

    selectedTypes,
    setSelectedTypes,

    selectedLevels,
    setSelectedLevels,

    selectedSeason,
    setSelectedSeason,

    startAfter,
    setStartAfter,

    startBefore,
    setStartBefore,

    allDisciplines,
    allOrganisateurs,
    allTypes,
    allLevels,
    allSeasons,

    activeFilterCount,
    toggleValue,
    clearFilters,
  } = filters;

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      document.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10020]">
      <button
        type="button"
        aria-label="Fermer les filtres"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/65 backdrop-blur-sm"
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="calendar-filters-title"
        className={[
          "absolute bottom-0 right-0 top-0 flex w-full flex-col",
          "bg-slate-50 shadow-[-20px_0_60px_rgba(15,23,42,0.28)]",
          "dark:bg-slate-950",
          "sm:max-w-[500px]",
        ].join(" ")}
      >
        <header className="flex shrink-0 items-center justify-between gap-4 border-b border-slate-200 bg-white px-4 py-4 dark:border-white/10 dark:bg-slate-900">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400">
              <FaFilter />
            </div>

            <div>
              <h2
                id="calendar-filters-title"
                className="font-black text-slate-900 dark:text-white"
              >
                Filtres du calendrier
              </h2>

              <p className="text-xs text-slate-500 dark:text-slate-400">
                {activeFilterCount} filtre
                {activeFilterCount > 1
                  ? "s"
                  : ""}{" "}
                actif
                {activeFilterCount > 1
                  ? "s"
                  : ""}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-white/10 dark:hover:text-white"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </header>

        <div className="flex-1 space-y-3 overflow-y-auto p-3 sm:p-4">
          <section className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-slate-900">
            <label className="mb-2 block text-xs font-black uppercase tracking-[0.12em] text-slate-700 dark:text-slate-200">
              Recherche
            </label>

            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 focus-within:border-red-400 focus-within:ring-2 focus-within:ring-red-100 dark:border-white/10 dark:bg-slate-800 dark:focus-within:ring-red-950/50">
              <FaSearch className="shrink-0 text-slate-400" />

              <input
                type="search"
                value={search}
                onChange={(event) =>
                  setSearch(
                    event.target.value
                  )
                }
                placeholder="Titre, discipline, lieu, organisateur..."
                className="w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 dark:text-white"
              />
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-slate-900">
            <h3 className="mb-3 text-xs font-black uppercase tracking-[0.12em] text-slate-700 dark:text-slate-200">
              Période
            </h3>

            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-[11px] font-bold text-slate-500 dark:text-slate-400">
                  Saison
                </label>

                <select
                  value={selectedSeason}
                  onChange={(event) =>
                    setSelectedSeason(
                      event.target.value
                    )
                  }
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-red-400 dark:border-white/10 dark:bg-slate-800 dark:text-white"
                >
                  <option value="">
                    Toutes les saisons
                  </option>

                  {allSeasons.map(
                    (season) => (
                      <option
                        key={season}
                        value={season}
                      >
                        Saison {season}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500 dark:text-slate-400">
                    À partir du
                  </label>

                  <input
                    type="date"
                    value={startAfter}
                    onChange={(event) =>
                      setStartAfter(
                        event.target.value
                      )
                    }
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-red-400 dark:border-white/10 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500 dark:text-slate-400">
                    Jusqu’au
                  </label>

                  <input
                    type="date"
                    value={startBefore}
                    onChange={(event) =>
                      setStartBefore(
                        event.target.value
                      )
                    }
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-red-400 dark:border-white/10 dark:bg-slate-800 dark:text-white"
                  />
                </div>
              </div>
            </div>
          </section>

          <FilterGroup
            title="Disciplines"
            values={allDisciplines}
            selectedValues={
              selectedDisciplines
            }
            onToggle={(value) =>
              toggleValue(
                value,
                setSelectedDisciplines
              )
            }
            onSelectAll={() =>
              setSelectedDisciplines(
                allDisciplines
              )
            }
            onClear={() =>
              setSelectedDisciplines([])
            }
            columns="grid-cols-2"
          />

          <FilterGroup
            title="Catégories d’âge"
            values={AGE_CATEGORIES}
            selectedValues={selectedAges}
            onToggle={(value) =>
              toggleValue(
                value,
                setSelectedAges
              )
            }
            onSelectAll={() =>
              setSelectedAges(
                AGE_CATEGORIES
              )
            }
            onClear={() =>
              setSelectedAges([])
            }
            columns="grid-cols-2"
          />

          <FilterGroup
            title="Organisateurs"
            values={allOrganisateurs}
            selectedValues={
              selectedOrganisateurs
            }
            onToggle={(value) =>
              toggleValue(
                value,
                setSelectedOrganisateurs
              )
            }
            onSelectAll={() =>
              setSelectedOrganisateurs(
                allOrganisateurs
              )
            }
            onClear={() =>
              setSelectedOrganisateurs([])
            }
          />

          <FilterGroup
            title="Types d’événements"
            values={allTypes}
            selectedValues={selectedTypes}
            onToggle={(value) =>
              toggleValue(
                value,
                setSelectedTypes
              )
            }
            onSelectAll={() =>
              setSelectedTypes(allTypes)
            }
            onClear={() =>
              setSelectedTypes([])
            }
          />

          <FilterGroup
            title="Niveaux"
            values={allLevels}
            selectedValues={
              selectedLevels
            }
            onToggle={(value) =>
              toggleValue(
                value,
                setSelectedLevels
              )
            }
            onSelectAll={() =>
              setSelectedLevels(
                allLevels
              )
            }
            onClear={() =>
              setSelectedLevels([])
            }
          />
        </div>

        <footer className="shrink-0 border-t border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-slate-900 sm:p-4">
          <div className="grid grid-cols-[auto_1fr] gap-2">
            <button
              type="button"
              onClick={clearFilters}
              className={[
                "inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border px-4",
                "border-slate-200 bg-white text-sm font-bold text-slate-600",
                "transition hover:bg-slate-50",
                "dark:border-white/10 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-white/10",
              ].join(" ")}
            >
              <FaTrashAlt />
              Effacer
            </button>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-red-600 px-4 text-sm font-black text-white shadow-lg shadow-red-600/20 transition hover:bg-red-700"
            >
              <FaCheck />

              <span>
                Voir {filteredEventsCount}{" "}
                événement
                {filteredEventsCount > 1
                  ? "s"
                  : ""}
              </span>
            </button>
          </div>
        </footer>
      </aside>
    </div>
  );
}